import type { Country } from './data.js';

import { UKRPOSHTA_COUNTRIES } from './data.js';

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const VALID_COUNTRY_CODES = new Set(UKRPOSHTA_COUNTRIES.map(c => c.id.toUpperCase()));

export const isValidCountryCode = (code: string | null | undefined): boolean => {
  if (!code) return false;
  return VALID_COUNTRY_CODES.has(code.trim().toUpperCase());
};

const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 65;

const collatorCache = new Map<string, Intl.Collator>();

const getCollator = (locale: string): Intl.Collator => {
  const normalized = locale?.trim() || 'en';
  const cacheKey = normalized.toLowerCase();
  const existing = collatorCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const collator = new Intl.Collator(normalized, {
    sensitivity: 'base',
    usage: 'sort',
    numeric: false,
    ignorePunctuation: true,
  });
  collatorCache.set(cacheKey, collator);
  return collator;
};

export const flagEmoji = (iso2: string | null | undefined): string => {
  if (!iso2 || iso2.length !== 2) {
    return '';
  }

  const codePoints: number[] = [];
  for (const char of iso2.toUpperCase()) {
    const charCode = char.codePointAt(0);
    if (!charCode || charCode < 65 || charCode > 90) {
      return '';
    }
    codePoints.push(REGIONAL_INDICATOR_OFFSET + charCode);
  }

  return String.fromCodePoint(...codePoints);
};

export const resolveLocalizedCountryName = (
  country: Country,
  t: TranslateFn,
): string => {
  const translated = t(`country.${country.id}`, { defaultValue: country.englishName });
  const trimmed = translated?.trim();
  return trimmed?.length ? trimmed : country.englishName;
};

export const formatCountryLabel = (
  country: Country,
  t: TranslateFn,
): string => {
  const localizedName = resolveLocalizedCountryName(country, t);
  const englishName = country.englishName.trim();
  const isSameName = localizedName.localeCompare(englishName, undefined, { sensitivity: 'base' }) === 0;
  const displayName = isSameName ? englishName : `${localizedName} — ${englishName}`;
  const flag = flagEmoji(country.id);
  return `${flag ? `${flag} ` : ''}${displayName}`.trim();
};

export const localeAwareSort = <T>(
  items: ReadonlyArray<T>,
  locale: string,
  selector: (item: T) => string,
): T[] => {
  const collator = getCollator(locale);
  const comparator = (a: T, b: T) => collator.compare(selector(a), selector(b));
  const copy = Array.from(items);
  copy.sort(comparator);
  return copy;
};
